import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  getPeriodBounds,
  isCurrentPeriod,
  isFuturePeriod,
  normalizeDateForPeriod,
  shiftDateByPeriod,
  type TimePeriod,
} from "@/components/charts";

interface TrackerTimeControlsProps {
  selectedPeriod: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  metricColor: string;
  variant?: "compact" | "full";
}

const periods: { id: TimePeriod; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Generate years from 2020 to current year + 5
const currentYear = new Date().getFullYear();
const years = Array.from(
  { length: currentYear - 2020 + 6 },
  (_, i) => 2020 + i
);

export const TrackerTimeControls = ({
  selectedPeriod,
  onPeriodChange,
  currentDate,
  onDateChange,
  metricColor,
  variant = "full",
}: TrackerTimeControlsProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(currentDate);

  useEffect(() => {
    setCalendarMonth(currentDate);
  }, [currentDate]);

  const formatDateDisplay = (date: Date, period: TimePeriod): string => {
    switch (period) {
      case "day":
        return date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
      case "week": {
        const { start, end } = getPeriodBounds("week", date);
        return `${start.toLocaleDateString("en-US", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-US", { day: "numeric", month: "short" })}`;
      }
      case "month":
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      case "year":
        return date.getFullYear().toString();
    }
  };

  const navigateDate = (direction: "prev" | "next") => {
    const multiplier = direction === "prev" ? -1 : 1;
    const newDate = shiftDateByPeriod(selectedPeriod, currentDate, multiplier);

    if (direction === "next" && isFuturePeriod(selectedPeriod, newDate)) {
      return;
    }

    onDateChange(newDate);
  };

  const goToToday = () => {
    const today = normalizeDateForPeriod(selectedPeriod, new Date());
    onDateChange(today);
    setCalendarMonth(today);
  };

  const isToday = (): boolean => isCurrentPeriod(selectedPeriod, currentDate);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onDateChange(normalizeDateForPeriod(selectedPeriod, date));
      setCalendarOpen(false);
    }
  };

  const handleMonthSelect = (date: Date | undefined) => {
    if (date) {
      const newDate = normalizeDateForPeriod("month", date);
      onDateChange(newDate);
      setCalendarOpen(false);
    }
  };

  const handleMonthChange = (monthIndex: string) => {
    const newMonth = parseInt(monthIndex);
    const newDate = new Date(calendarMonth);
    newDate.setMonth(newMonth);
    setCalendarMonth(newDate);
  };

  const handleYearChange = (year: string) => {
    const newYear = parseInt(year);
    const newDate = new Date(calendarMonth);
    newDate.setFullYear(newYear);
    setCalendarMonth(newDate);
  };

  // Get appropriate caption for the calendar based on period
  const getCalendarCaption = () => {
    switch (selectedPeriod) {
      case "day":
        return "Select a date";
      case "week":
        return "Select a week";
      case "month":
        return "Select a month";
      case "year":
        return "Select a year";
    }
  };

  // Calendar content with month/year selectors
  const CalendarContent = () => (
    <div className="p-3">
      {/* Month/Year Selectors */}
      <div className="flex items-center justify-center gap-2 pb-3 border-b border-border mb-3">
        <Select
          value={calendarMonth.getMonth().toString()}
          onValueChange={handleMonthChange}
        >
          <SelectTrigger className="w-[120px] h-8 text-sm">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {months.map((month, index) => (
              <SelectItem
                key={month}
                value={index.toString()}
                className="text-sm"
              >
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={calendarMonth.getFullYear().toString()}
          onValueChange={handleYearChange}
        >
          <SelectTrigger className="w-[90px] h-8 text-sm">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {years.map((year) => (
              <SelectItem
                key={year}
                value={year.toString()}
                className="text-sm"
              >
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Calendar */}
      <CalendarComponent
        mode="single"
        selected={currentDate}
        onSelect={
          selectedPeriod === "month" ? handleMonthSelect : handleDateSelect
        }
        month={calendarMonth}
        onMonthChange={setCalendarMonth}
        initialFocus
        classNames={{
          nav: "flex items-center justify-between gap-1 pb-2",
          month_caption:
            "flex justify-center items-center text-sm font-medium text-foreground",
          button_previous:
            "h-7 w-7 flex items-center justify-center rounded-md border border-input hover:bg-muted",
          button_next:
            "h-7 w-7 flex items-center justify-center rounded-md border border-input hover:bg-muted",
        }}
      />
    </div>
  );

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-2">
        {/* Period Selector */}
        <div className="flex items-center bg-muted/50 rounded-md p-0.5">
          {periods.map((period) => (
            <button
              key={period.id}
              onClick={() => onPeriodChange(period.id)}
              className={cn(
                "px-2 py-1 text-xs font-medium rounded-sm transition-all duration-200",
                selectedPeriod === period.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {period.label}
            </button>
          ))}
        </div>

        {/* Date Navigation with Picker */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate("prev")}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Date Picker Popover */}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 bg-muted/30 rounded-md",
                  "hover:bg-muted/50 transition-colors cursor-pointer",
                  "text-xs font-medium text-foreground whitespace-nowrap"
                )}
              >
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{formatDateDisplay(currentDate, selectedPeriod)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 bg-popover border-border"
              align="center"
            >
              <div className="p-3 border-b border-border">
                <p className="text-sm font-medium text-foreground">
                  {getCalendarCaption()}
                </p>
              </div>
              <CalendarContent />
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate("next")}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Today Button */}
        {!isToday() && (
          <Button
            variant="ghost"
            size="sm"
            onClick={goToToday}
            className="h-7 text-xs font-medium hover:bg-muted"
            style={{ color: metricColor }}
          >
            Today
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
        {periods.map((period) => (
          <button
            key={period.id}
            onClick={() => onPeriodChange(period.id)}
            className={cn(
              "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
              selectedPeriod === period.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            {period.label}
          </button>
        ))}
      </div>

      {/* Date Navigation with Picker */}
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigateDate("prev")}
          className="h-9 w-9 shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Date Picker Popover */}
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg flex-1 justify-center min-w-0",
                "hover:bg-muted transition-colors cursor-pointer"
              )}
            >
              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground truncate">
                {formatDateDisplay(currentDate, selectedPeriod)}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 bg-popover border-border"
            align="center"
          >
            <div className="p-3 border-b border-border">
              <p className="text-sm font-medium text-foreground">
                {getCalendarCaption()}
              </p>
            </div>
            <CalendarContent />
          </PopoverContent>
        </Popover>

        <Button
          variant="outline"
          size="icon"
          onClick={() => navigateDate("next")}
          className="h-9 w-9 shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Today Button */}
      {!isToday() && (
        <Button
          variant="ghost"
          size="sm"
          onClick={goToToday}
          className="w-full text-xs font-medium hover:bg-muted"
          style={{ color: metricColor }}
        >
          Jump to Today
        </Button>
      )}
    </div>
  );
};
